import React, { useEffect, useMemo, useState } from "react";
import KnowledgeHubLayout from "../../layouts/KnowledgeHubLayout";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import { Helmet } from "react-helmet-async";
import { LuLightbulb } from "react-icons/lu";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";

// Helper to extract the first non-empty line (used as the question/title)
const extractFirstLine = (markdown) => {
	if (!markdown) return "Untitled";
	const lines = markdown.split(/\r?\n/).map((l) => l.trim());
	const first = lines.find((l) => l.length > 0) || "Untitled";
	// Strip common emphasis markers to make a cleaner list label
	return first.replace(/^\*\*|\*\*$/g, "").replace(/^#\s*/, "");
};

// Helper: extract a short description from the markdown (first paragraph after title)
const extractDescription = (markdown) => {
	if (!markdown) return "";
	const lines = markdown.split(/\r?\n/).map((l) => l.trim());
	// remove leading empty lines
	while (lines.length && lines[0] === "") lines.shift();
	// drop first non-empty line (treated as title)
	if (lines.length) lines.shift();
	// collect until blank line
	const para = [];
	for (const l of lines) {
		if (!l) break;
		para.push(l);
	}
	const text = para.join(" ").replace(/[#*_>`~]/g, "").replace(/\s+/g, " ");
	return text.slice(0, 300);
};

// Slugify a string: lower-case, replace non-alphanumerics with hyphens, collapse dashes
const slugify = (s) =>
	(s || "")
		.toLowerCase()
		.normalize("NFKD")
		.replace(/[\u0300-\u036f]/g, "")
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/-{2,}/g, "-")
		.replace(/^-+|-+$/g, "");

// Helper to sort files so the one with the largest numeric prefix (e.g., 12_*.md) is first
const sortByNumericPrefixDesc = (a, b) => {
	const getNum = (name) => {
		const m = name.match(/^(\d+)/);
		return m ? parseInt(m[1], 10) : -Infinity;
	};
	return getNum(b.filename) - getNum(a.filename);
};

const KnowledgeHub = () => {
	const { slug } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const baseUrl = "https://paralegal.lk"; // used for canonical tags

	// Discover all markdown files in the Questions folder at build time
	const files = useMemo(() => {
		try {
			const ctx = require.context("./Questions", false, /\.md$/);
			const items = ctx.keys().map((k) => ({
				key: k,
				filename: k.replace(/^\.\//, ""),
				url: ctx(k), // This will be a URL string emitted by file-loader
			}));
			return items.sort(sortByNumericPrefixDesc);
		} catch (e) {
			console.error("Failed to load KnowledgeHub questions:", e);
			return [];
		}
	}, []);

	const [list, setList] = useState([]); // { filename, url, title, slug }[]
	const [active, setActive] = useState(null); // { filename, url, title, slug, content, description }
		const [loading, setLoading] = useState(true);
		const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
		const [isListOpen, setIsListOpen] = useState(false);

	// Keep track of viewport to toggle mobile/desktop behaviors
	useEffect(() => {
		const onResize = () => setIsDesktop(window.innerWidth >= 768);
		window.addEventListener("resize", onResize);
		return () => window.removeEventListener("resize", onResize);
	}, []);

	// Open a selected item and load its content
	const openItem = async (item) => {
		try {
			setLoading(true);
			const resp = await fetch(item.url);
			const text = await resp.text();
			const title = item.title || extractFirstLine(text);
			const description = extractDescription(text);
			setActive({ ...item, title, content: text, description });
		} catch (e) {
			console.error("Failed to load KnowledgeHub item:", e);
		} finally {
			setLoading(false);
		}
	};

	// Load titles (first line of each file) and default selection
	useEffect(() => {
		let cancelled = false;
		const load = async () => {
			if (files.length === 0) {
				setLoading(false);
				return;
			}

			try {
				const metas = await Promise.all(
					files.map(async (f) => {
						try {
							const resp = await fetch(f.url);
							const text = await resp.text();
							const title = extractFirstLine(text);
							const sBare = slugify(title);
							const numMatch = f.filename.match(/^(\d+)/);
							const s = numMatch ? `${numMatch[1]}-${sBare}` : sBare;
							return { ...f, title, slug: s };
						} catch {
							const fallbackTitle = f.filename;
							const sBare = slugify(fallbackTitle);
							const numMatch = f.filename.match(/^(\d+)/);
							const s = numMatch ? `${numMatch[1]}-${sBare}` : sBare;
							return { ...f, title: fallbackTitle, slug: s };
						}
					})
				);
				if (cancelled) return;
				setList(metas);

				// If a slug is present in the URL, open that one; otherwise open latest
				const pick = (slug && metas.find((m) => m.slug === slug)) || metas[0];
				if (pick) {
					const resp = await fetch(pick.url);
					const text = await resp.text();
					const title = pick.title || extractFirstLine(text);
					const description = extractDescription(text);
					if (!cancelled) setActive({ ...pick, title, content: text, description });
					// If no slug in URL, normalize URL to include it for canonical consistency
					if (!slug) {
						navigate(`/knowledge-hub/${pick.slug}`, { replace: true });
					}
				}
			} catch (e) {
				console.error(e);
			} finally {
				if (!cancelled) setLoading(false);
			}
		};
		load();
		return () => {
			cancelled = true;
		};
	}, [files]);

	// When slug changes (via navigation), load the corresponding item if list is ready
	useEffect(() => {
		if (!slug || list.length === 0) return;
		const match = list.find((m) => m.slug === slug);
		if (!match) return;
		// If active is already the match, do nothing
		if (active && active.slug === match.slug) return;
		openItem(match);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [slug, list]);

		return (
			<div className="relative">
				<Helmet>
					<title>{active?.title ? `${active.title} | Knowledge Hub | paralegal.lk` : "Knowledge Hub | paralegal.lk"}</title>
					<meta name="description" content={active?.description || "Curated legal Q&A and research notes."} />
					<link rel="canonical" href={`${baseUrl}${location.pathname}`} />
					{/* JSON-LD for QAPage to aid rich results */}
					{active?.title && active?.content && (
						<script type="application/ld+json">{JSON.stringify({
							'@context': 'https://schema.org',
							'@type': 'QAPage',
							mainEntity: {
								'@type': 'Question',
								name: active.title,
								acceptedAnswer: {
									'@type': 'Answer',
									text: active.content
								}
							}
						})}</script>
					)}
				</Helmet>

				{/* Sidebar content (desktop) passed to layout */}
				<KnowledgeHubLayout
					sidebar={
						<div className="w-full px-10">
							<aside className="pl-filter flex flex-col w-full text-pl-text-tertiary h-fit bg-pl-bg-elevated overflow-hidden border border-pl-border rounded-lg">
								{/* Info box (desktop) */}
								<div className="px-4 pt-4">
									<div role="note" className="text-12 leading-6 rounded-md border border-pl-border bg-pl-bg-subtle p-3">
										<span className="text-pl-text-secondary">Answers are generated by our AI research agent. For access, email </span>
										<a href="mailto:admin@paralegal.lk" className="text-pl-accent underline underline-offset-2 hover:opacity-90">admin@paralegal.lk</a>
									</div>
								</div>
								<div className="border-b mt-4 border-pl-border">
									<div className="p-4 flex justify-between items-center text-14 font-medium hover:bg-pl-bg-subtle">
										<span className="inline-flex items-center gap-2 text-pl-text-secondary">
											<LuLightbulb className="text-pl-accent" size={16} />
											Questions
										</span>
										{list.length > 0 && (
											<span className="text-12 text-pl-text-tertiary">{list.length}</span>
										)}
									</div>
								</div>
								<div className="p-4">
									<ul className="list-none w-full px-0 mb-0 flex flex-col gap-1 max-h-[60vh] overflow-y-auto pr-2">
										{list.map((item) => (
											<li key={item.filename}>
												<Link
													className={`block w-full text-left text-13 px-2 py-1 rounded transition-colors ${
														active && active.filename === item.filename
															? "bg-pl-bg-subtle text-pl-text-primary"
															: "text-pl-text-tertiary hover:bg-pl-bg-subtle"
													}`}
													to={`/knowledge-hub/${item.slug}`}
													title={item.title}
												>
													{item.title}
												</Link>
											</li>
										))}
									</ul>
								</div>
							</aside>
						</div>
					}
				>
					<div className="flex flex-col mx-auto w-full mt-0 p-0">
						<h1 className="mb-4 font-semibold text-18 md:text-22 flex items-center justify-between">
							<span>Knowledge Hub</span>
							{/* Mobile: icon button to open list; text label hidden on mobile */}
							{!isDesktop && list.length > 0 && (
								<button
									className="inline-flex items-center text-pl-accent ml-4"
										type="button"
										aria-controls="questions-section"
										aria-expanded={isListOpen}
										onClick={() => setIsListOpen((v) => !v)}
										aria-label="Open questions list"
									>
										<LuLightbulb className="text-pl-accent" size={18} />
									</button>
							)}
							</h1>

							{/* Main content */}
								{/* Mobile trigger moved into header above */}
								{loading && (
									<div className="text-14 text-pl-text-tertiary py-10">Loading…</div>
								)}
								{!loading && active && (
									<article className="max-w-none text-14 md:text-15 text-pl-text-primary">
					<ReactMarkdown
											remarkPlugins={[remarkGfm, remarkBreaks]}
											components={{
												p: ({ node, ...props }) => <p className="mb-4 leading-7" {...props} />,
												ul: ({ node, ...props }) => <ul className="list-disc pl-5 mb-4 space-y-1" {...props} />,
												ol: ({ node, ...props }) => <ol className="list-decimal pl-5 mb-4 space-y-1" {...props} />,
												li: ({ node, ...props }) => <li className="leading-7" {...props} />,
												h1: ({ node, ...props }) => <h1 className="text-20 md:text-24 font-semibold mt-2 mb-4" {...props} />,
												h2: ({ node, ...props }) => <h2 className="text-18 md:text-20 font-display font-semibold mt-6 mb-3" {...props} />,
												h3: ({ node, ...props }) => <h3 className="text-16 md:text-18 font-semibold mt-5 mb-2" {...props} />,
												h4: ({ node, ...props }) => (
													<h4 className="text-15 md:text-16 font-medium italic mt-4 mb-3" {...props} />
												),
												blockquote: ({ node, ...props }) => (
													<blockquote className="border-l-4 border-pl-accent/70 bg-pl-bg-subtle pl-4 py-2 pr-3 rounded mr-1 italic mb-4 text-pl-text-secondary" {...props} />
												),
												a: ({ node, ...props }) => (
													<a className="text-pl-accent underline underline-offset-2 hover:opacity-90 break-words" target="_blank" rel="noopener noreferrer" {...props} />
												),
												code: ({ node, inline, className, children, ...props }) => (
													<code
														className={
															(inline ? "px-1 py-0.5 rounded bg-pl-bg-subtle" : "block p-3 rounded bg-pl-bg-subtle overflow-auto") +
															(className ? ` ${className}` : "")
														}
														{...props}
													>
														{children}
													</code>
												),
												table: ({ node, ...props }) => (
													<div className="w-full overflow-x-auto mb-4">
														<table className="w-full border-collapse text-left text-13">
															{props.children}
														</table>
													</div>
												),
												thead: ({ node, ...props }) => <thead className="bg-pl-bg-elevated" {...props} />,
												tbody: ({ node, ...props }) => <tbody {...props} />,
												tr: ({ node, ...props }) => <tr className="border-b border-pl-border" {...props} />,
												th: ({ node, ...props }) => (
													<th
														className="px-3 py-2 font-semibold text-pl-text-secondary border border-pl-border"
														{...props}
													/>
												),
												td: ({ node, ...props }) => (
													<td className="px-3 py-2 align-top border border-pl-border" {...props} />
												),
												hr: ({ node, ...props }) => <hr className="my-6 border-pl-border" {...props} />,
											}}
										>
											{active.content}
										</ReactMarkdown>
									</article>
								)}
								{!loading && !active && (
									<div className="text-14 text-pl-text-tertiary py-10">No content available.</div>
								)}


							{/* Mobile slide-over for questions remains outside layout */}
										{/* Mobile slide-over for questions */}
										{!isDesktop && (
											<>
												<div
													className={`fixed inset-0 bg-black bg-opacity-30 dark:bg-opacity-50 z-[140] duration-300 ${
														isListOpen ? "block opacity-100" : "hidden opacity-0"
													}`}
													onClick={() => setIsListOpen(false)}
												/>
												<div
													id="questions-section"
													className={`fixed top-0 right-0 h-full bg-pl-bg z-[150] shadow-lg transform transition-transform duration-300 border-l border-pl-border w-[75%] sm:w-[420px] max-w-[90%] ${
														isListOpen ? "translate-x-0" : "translate-x-full"
													}`}
												>
													<div className="flex p-10 pr-20 justify-between items-center bg-pl-bg-elevated border-y border-pl-border">
														<div className="inline-flex items-center gap-2 text-16 text-pl-text-secondary font-medium">
															<LuLightbulb className="text-pl-accent" size={18} />
														</div>
														<button
															className="text-12 px-15 py-0.5 rounded-25 bg-pl-accent text-white border border-pl-accent"
															onClick={() => setIsListOpen(false)}
														>
															Save
														</button>
													</div>
													<div className="h-[calc(100%-60px)] overflow-y-auto p-10">
														{/* Info box (mobile) */}
														<div role="note" className="mb-4 text-12 leading-6 rounded-md border border-pl-border bg-pl-bg-subtle p-3">
															<span className="text-pl-text-secondary">Answers are generated by our AI research agent. For access, email </span>
															<a href="mailto:admin@paralegal.lk" className="text-pl-accent underline underline-offset-2 hover:opacity-90">admin@paralegal.lk</a>
														</div>
														<ul className="flex flex-col gap-2">
															{list.map((item) => (
																<li key={item.filename}>
																	<Link
																		className={`block w-full text-left text-13 px-2 py-1 rounded transition-colors ${
																			active && active.filename === item.filename
																				? "bg-pl-bg-subtle text-pl-text-primary"
																				: "text-pl-text-tertiary hover:bg-pl-bg-subtle"
																		}`}
																		to={`/knowledge-hub/${item.slug}`}
																		onClick={() => setIsListOpen(false)}
																		title={item.title}
																	>
																		{item.title}
																	</Link>
																</li>
															))}
														</ul>
													</div>
												</div>
											</>
										)}
						</div>

					</KnowledgeHubLayout>
			</div>

	);
};

export default KnowledgeHub;

